import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { type components, fileBody } from '@gftg/api-client';
import { api, unwrap, unwrapEmpty } from './api';

export type PublicEvent = components['schemas']['PublicEventDto'];
export type Program = components['schemas']['ProgramDto'];
export type Session = components['schemas']['SessionDto'];
export type SessionDetail = components['schemas']['SessionDetailDto'];
export type Track = components['schemas']['TrackDto'];
export type Speaker = components['schemas']['SpeakerDto'];
export type Venue = components['schemas']['VenueDto'];
export type ProfileOptions = components['schemas']['ProfileOptionsDto'];
export type Option = components['schemas']['OptionDto'];
export type CertificationOption = components['schemas']['CertificationOptionDto'];
export type Directory = components['schemas']['DirectoryDto'];
export type DirectoryPerson = components['schemas']['DirectoryPersonDto'];
export type DirectoryPersonDetail = components['schemas']['DirectoryPersonDetailDto'];
export type DirectoryOption = components['schemas']['DirectoryOptionDto'];
export type Matches = components['schemas']['MatchesDto'];
export type Match = components['schemas']['MatchDto'];
export type AnnouncementFeed = components['schemas']['AnnouncementFeedDto'];
export type Announcement = components['schemas']['AnnouncementDto'];
export type AttendeeMe = components['schemas']['AttendeeMeDto'];
export type SignInResult = components['schemas']['AttendeeSignInDto'];
export type SignInChoice = components['schemas']['AttendeeChoiceDto'];
export type MyProfile = components['schemas']['MyProfileDto'];
export type ProfileUpdate = components['schemas']['UpdateMyProfileDto'];
export type Schedule = components['schemas']['ScheduleDto'];
export type CalendarSubscription = components['schemas']['CalendarSubscriptionDto'];
export type UnsubscribeState = components['schemas']['UnsubscribeStateDto'];

export const sessionKey = ['auth', 'me'] as const;

/** The consume response for this emailed token, including ACC-04's choose-person list. */
export const signInResultKey = (token: string) => ['auth', 'sign-in', token] as const;

/**
 * Who is signed in (ACC-01). The cookie is HttpOnly, so this answer is the only source of truth:
 * nothing the browser remembers can paper over a session the API has stopped accepting.
 */
export function useAttendee(enabled = true) {
  return useQuery({
    queryKey: sessionKey,
    queryFn: (): Promise<AttendeeMe> =>
      unwrap('Your session could not be read.', api.GET('/auth/me')),
    // A 401 here is the normal signed-out state, not a failure worth retrying or reporting.
    retry: false,
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useRequestSignInLink() {
  return useMutation({
    mutationFn: (email: string) =>
      unwrap(
        'The sign-in link could not be requested.',
        api.POST('/auth/request-link', { body: { email } }),
      ),
  });
}

/**
 * Spends the emailed token. Answers either with a session or with the people registered under that
 * address (ACC-04), in which case the same token is sent again with the one the attendee picked.
 */
const consumeInflight = new Map<string, Promise<SignInResult>>();

function consumeSignInLink(token: string, personId?: string): Promise<SignInResult> {
  const key = `${token}:${personId ?? ''}`;
  const existing = consumeInflight.get(key);
  if (existing) {
    return existing;
  }

  const request = unwrap(
    'This sign-in link could not be used.',
    api.POST('/auth/consume', { body: { token, ...(personId ? { personId } : {}) } }),
  ).finally(() => {
    consumeInflight.delete(key);
  });

  consumeInflight.set(key, request);
  return request;
}

/**
 * StrictMode drops a mutation's observer between the two effect passes of a development mount.
 * `choose_person` has no session cookie to fall back on, so the result has to live in the query
 * cache — `onSuccess` belongs to the observer that just disappeared.
 */
export function useSignInResult(token: string) {
  return useQuery({
    queryKey: signInResultKey(token),
    queryFn: (): Promise<SignInResult> =>
      Promise.reject(new Error('Seeded by consume, never fetched.')),
    enabled: false,
    staleTime: Infinity,
    retry: false,
  });
}

export function useConsumeSignInLink() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ token, personId }: { token: string; personId?: string }) => {
      const result = await consumeSignInLink(token, personId);
      client.setQueryData(signInResultKey(token), result);
      if (result.attendee) {
        client.setQueryData(sessionKey, result.attendee);
      }
      return result;
    },
  });
}

export function useSignOut() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => unwrapEmpty('Signing out failed.', api.POST('/auth/sign-out')),
    // On settled rather than on success: a session the API had already dropped answers 401, and
    // leaving the app looking signed in after they asked to leave is the one unacceptable outcome.
    onSettled: () => client.clear(),
  });
}

/** Anything readable without signing in comes from /public, so no request here carries identity. */
export function useEvent() {
  return useQuery({
    queryKey: ['event'],
    queryFn: (): Promise<PublicEvent> =>
      unwrap('The event could not be loaded.', api.GET('/public/event')),
    // The shell depends on this: branding, day list and tiles all come from here.
    staleTime: 5 * 60_000,
  });
}

export interface ProgramFilters {
  day?: string;
  trackId?: string;
  q?: string;
}

export function useProgram(filters: ProgramFilters = {}, options: { enabled?: boolean } = {}) {
  const query = {
    ...(filters.day ? { day: filters.day } : {}),
    ...(filters.trackId ? { trackId: filters.trackId } : {}),
    ...(filters.q ? { q: filters.q } : {}),
  };

  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: ['program', query],
    queryFn: (): Promise<Program> =>
      unwrap('The program could not be loaded.', api.GET('/public/program', { params: { query } })),
    // A room or time change during the event must not sit behind a long cache (PRG-08).
    staleTime: 30_000,
  });
}

/** Named for the detail screen, so it is never confused with the sign-in `useSession`. */
export function useSessionDetail(idOrSlug: string) {
  return useQuery({
    queryKey: ['session', idOrSlug],
    queryFn: (): Promise<SessionDetail> =>
      unwrap(
        'This session is not in the published program.',
        api.GET('/public/program/sessions/{idOrSlug}', { params: { path: { idOrSlug } } }),
      ),
    staleTime: 30_000,
  });
}

export function useSpeakers() {
  return useQuery({
    queryKey: ['speakers'],
    queryFn: (): Promise<Speaker[]> =>
      unwrap('The presenters could not be loaded.', api.GET('/public/speakers')),
  });
}

export function useSpeaker(id: string) {
  return useQuery({
    queryKey: ['speaker', id],
    queryFn: (): Promise<Speaker> =>
      unwrap(
        'This presenter is not in the published program.',
        api.GET('/public/speakers/{id}', { params: { path: { id } } }),
      ),
  });
}

/** The questionnaire's lists (PRF-03). Organizers edit them, so they are fetched, not bundled. */
export function useProfileOptions() {
  return useQuery({
    queryKey: ['profile-options'],
    queryFn: (): Promise<ProfileOptions> =>
      unwrap('The questionnaire could not be loaded.', api.GET('/public/profile-options')),
    staleTime: 5 * 60_000,
  });
}

export const profileKey = ['profile'] as const;

/**
 * The attendee's own profile (module 5). Distinct from `usePerson`, which is the card other
 * attendees see: this one carries their own address and their own free-text ask, and only ever
 * describes whoever holds the session.
 */
export function useMyProfile() {
  return useQuery({
    queryKey: profileKey,
    queryFn: (): Promise<MyProfile> =>
      unwrap('Your profile could not be loaded.', api.GET('/profile')),
    // Theirs alone, so nothing else can have changed it; refetching on every screen would only
    // race the answers they are still typing.
    staleTime: 5 * 60_000,
    gcTime: 0,
  });
}

/**
 * One save. The response is the whole profile as stored, which is seeded into the cache rather than
 * invalidated: a refetch here would arrive after the next keystroke and overwrite it.
 */
export function useUpdateMyProfile() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (body: ProfileUpdate): Promise<MyProfile> =>
      unwrap('Your answers could not be saved.', api.PATCH('/profile', { body })),
    onSuccess: (profile) => {
      client.setQueryData(profileKey, profile);
      // Hiding themselves, or leaving Concierge Connections, changes what these two return about
      // them.
      void client.invalidateQueries({ queryKey: ['directory'] });
      void client.invalidateQueries({ queryKey: ['matches'] });
    },
  });
}

/**
 * Their photograph (PRF-02), which is a separate mutation from the rest of the profile because it
 * is a file rather than a field. Same treatment of the response: the API answers with the whole
 * profile, so the completion figure moves in the same render as the picture.
 */
export function useSetMyPhoto() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (file: File): Promise<MyProfile> =>
      unwrap(
        'Your photo could not be uploaded.',
        api.PUT('/profile/photo', { body: fileBody(file) }),
      ),
    onSuccess: onPhotoChanged(client),
  });
}

export function useRemoveMyPhoto() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (): Promise<MyProfile> =>
      unwrap('Your photo could not be removed.', api.DELETE('/profile/photo')),
    onSuccess: onPhotoChanged(client),
  });
}

/**
 * An attendee's face is on three other screens, and they hold their own copies of it. Invalidating
 * rather than patching each: the attendee has just watched an upload finish, so a moment's refetch
 * is invisible, and there is no half-typed state here for a late response to trample.
 */
function onPhotoChanged(client: ReturnType<typeof useQueryClient>) {
  return (profile: MyProfile) => {
    client.setQueryData(profileKey, profile);
    void client.invalidateQueries({ queryKey: ['directory'] });
    void client.invalidateQueries({ queryKey: ['matches'] });
  };
}

export const scheduleKey = ['schedule'] as const;

/**
 * Their personal schedule (module 4). Enabled only once they are signed in: the agenda itself is
 * public (ACC-07), so an anonymous visitor reaches the screens that draw stars and there is nothing
 * to ask about yet.
 */
export function useSchedule(enabled = true) {
  return useQuery({
    queryKey: scheduleKey,
    queryFn: (): Promise<Schedule> =>
      unwrap('Your schedule could not be loaded.', api.GET('/schedule')),
    // The sessions in it can move under the attendee (PRG-08), and the sessions themselves are what
    // this returns rather than references to a cache the program owns.
    staleTime: 30_000,
    gcTime: 0,
    enabled,
  });
}

/**
 * Starring and unstarring, and the two calendar subscription routes. All four answer with the whole
 * schedule, so all four seed the cache the same way instead of refetching: a star that goes dark for
 * one round trip after the attendee taps it reads as a tap that did not register.
 */
export function useScheduleActions() {
  const client = useQueryClient();
  const seed = (schedule: Schedule) => client.setQueryData(scheduleKey, schedule);

  const save = useMutation({
    mutationFn: (id: string): Promise<Schedule> =>
      unwrap(
        'That session could not be saved.',
        api.PUT('/schedule/sessions/{id}', { params: { path: { id } } }),
      ),
    onSuccess: seed,
  });

  const remove = useMutation({
    mutationFn: (id: string): Promise<Schedule> =>
      unwrap(
        'That session could not be removed.',
        api.DELETE('/schedule/sessions/{id}', { params: { path: { id } } }),
      ),
    onSuccess: seed,
  });

  const subscribe = useMutation({
    mutationFn: (): Promise<Schedule> =>
      unwrap('The calendar subscription could not be created.', api.POST('/schedule/calendar')),
    onSuccess: seed,
  });

  const unsubscribe = useMutation({
    mutationFn: (): Promise<Schedule> =>
      unwrap('The calendar subscription could not be revoked.', api.DELETE('/schedule/calendar')),
    onSuccess: seed,
  });

  return { save, remove, subscribe, unsubscribe };
}

/**
 * MSG-03, and the one part of the app that works with no session at all. They may be reading the
 * email on a laptop they have never signed in on, and making them sign in first to stop unwanted
 * mail would be the product arguing with them.
 *
 * The signed link in the URL is the whole of the authority, so `retry: false`: a link that does not
 * verify will not verify on the third attempt either, and it is the same 404 whether the signature
 * is wrong or the message is gone.
 */
export function useUnsubscribeState(messageId: string, signature: string) {
  return useQuery({
    queryKey: ['unsubscribe', messageId],
    queryFn: (): Promise<UnsubscribeState> =>
      unwrap(
        'That unsubscribe link could not be read.',
        api.GET('/unsubscribe/{messageId}/{signature}', {
          params: { path: { messageId, signature } },
        }),
      ),
    retry: false,
    gcTime: 0,
  });
}

export function useUnsubscribeActions(messageId: string, signature: string) {
  const client = useQueryClient();
  const path = { params: { path: { messageId, signature } } };
  const seed = (state: UnsubscribeState) =>
    client.setQueryData(['unsubscribe', messageId], state);

  const unsubscribe = useMutation({
    mutationFn: (): Promise<UnsubscribeState> =>
      unwrap(
        'You could not be unsubscribed.',
        api.POST('/unsubscribe/{messageId}/{signature}', path),
      ),
    onSuccess: seed,
  });

  const resubscribe = useMutation({
    mutationFn: (): Promise<UnsubscribeState> =>
      unwrap(
        'You could not be resubscribed.',
        api.DELETE('/unsubscribe/{messageId}/{signature}', path),
      ),
    onSuccess: seed,
  });

  return { unsubscribe, resubscribe };
}

export interface DirectoryFilters {
  q?: string;
  industryId?: string;
  certificationId?: string;
}

/** One page is plenty for a phone; Q-01 plans for 400 people, and 400 cards is not a screen. */
const DIRECTORY_PAGE = 40;

/**
 * The one query in the app that asks for people rather than the program, which is why it goes to
 * `/directory` instead of `/public` (ACC-07) and why nothing about it is cached.
 */
export function useDirectory(filters: DirectoryFilters = {}) {
  const query = {
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.industryId ? { industryId: filters.industryId } : {}),
    ...(filters.certificationId ? { certificationId: filters.certificationId } : {}),
  };

  return useInfiniteQuery({
    queryKey: ['directory', query],
    initialPageParam: 0,
    queryFn: ({ pageParam }): Promise<Directory> =>
      unwrap(
        'The directory could not be loaded.',
        api.GET('/directory', {
          params: { query: { ...query, limit: DIRECTORY_PAGE, offset: pageParam } },
        }),
      ),
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((total, page) => total + page.people.length, 0);
      return loaded < last.matched ? loaded : undefined;
    },
    // Each keystroke is a new key, and a list that blanks between them is unreadable: the
    // previous people stay on screen until the new ones arrive.
    placeholderData: keepPreviousData,
    // Someone who registered this morning should be findable this morning (SYNC-07).
    staleTime: 60_000,
    // Nothing about who is attending outlives the screen it was fetched for.
    gcTime: 0,
  });
}

export function usePerson(id: string) {
  return useQuery({
    queryKey: ['directory-person', id],
    queryFn: (): Promise<DirectoryPersonDetail> =>
      unwrap(
        'That attendee is not in the directory.',
        api.GET('/directory/{id}', { params: { path: { id } } }),
      ),
    staleTime: 60_000,
    gcTime: 0,
  });
}

/**
 * Concierge Connections (module 7). Whose introductions these are comes from the session cookie,
 * so there is nothing to pass and nothing to change into somebody else.
 */
export function useMatches() {
  return useQuery({
    queryKey: ['matches'],
    queryFn: (): Promise<Matches> =>
      unwrap('Your matches could not be loaded.', api.GET('/matches')),
    staleTime: 60_000,
    gcTime: 0,
  });
}

/** Announcements (module 8). Targeted ones belong to a group, which the session already names. */
export function useAnnouncements() {
  return useQuery({
    queryKey: ['announcements'],
    queryFn: (): Promise<AnnouncementFeed> =>
      unwrap('The announcements could not be loaded.', api.GET('/announcements')),
    // A shuttle time that moved half an hour ago is the whole point of this feed.
    staleTime: 30_000,
    gcTime: 0,
  });
}

export function useVenue() {
  return useQuery({
    queryKey: ['venue'],
    queryFn: (): Promise<Venue> =>
      unwrap('The venue information could not be loaded.', api.GET('/public/venue')),
    staleTime: 5 * 60_000,
  });
}
